import '../App.css';
import React, { useState, useRef, useEffect, lazy, Suspense } from "react";    
import { Routes, Route, useLocation, Navigate } from 'react-router-dom';
import WebpageHome from './WebpageHome';
import SignIn from './SignIn/MagicLinkSignIn.js';
import PrivateRoute from './PrivateRoute';
import 'firebase/compat/auth';
import 'firebase/compat/firestore';
import { LicenseInfo } from '@mui/x-license';
import ResponsiveAppBar from './ResponsiveAppBar.js'
import { useUser } from './UserContext';
import { useCourtDates } from './CourtDateProvider';
import logger from '../utils/logger';
// import PropertyTaxWidget from '@propriety/property-tax-widget';
import Card from '@mui/material/Card';
import CircularProgress from '@mui/material/CircularProgress';
import Box from '@mui/material/Box';
import LinkAuthTest from './LinkAuthTest.js';
import IndividualAnalysisContainer from './CaseReview/IndividualAnalysisContainer.js';
import CaseAnalysisContainer from './CaseReview/CaseAnalysisContainer.js';

// Lazy load heavy components
const Visualizations = lazy(() => import('./Visualizations/Visualizations'));
const InvoicingAnalysisPage = lazy(() => import('./InvoicingAnalysis'));
const ChatContainer = lazy(() => import('./Chat/ChatContainer'));
const TownViewContainer = lazy(() => import('./TownView/TownViewContainer.js'));
const SnapPage = lazy(() => import('../features/snap/SnapPage'));

// Lazy load GoogleMapsProvider only when needed
const GoogleMapsProvider = lazy(() => 
  import('./GoogleMapsProvider').then(module => ({ default: module.GoogleMapsProvider }))
);

// Loading fallback component
const LoadingFallback = () => (
  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
    <CircularProgress />
  </Box>
);

// Handles auth:redirect from axios/fetch interceptors (non-React callers). Renders
// <Navigate replace /> so session failures get client-side routing, not a reload.
const AuthRedirectHandler = () => {
  const [redirectPath, setRedirectPath] = useState(null);

  useEffect(() => {
    const handleAuthRedirect = (event) => {
      const { path } = event.detail;
      setRedirectPath(path);
    };

    window.addEventListener('auth:redirect', handleAuthRedirect);

    return () => {
      window.removeEventListener('auth:redirect', handleAuthRedirect);
    };
  }, []);

  if (redirectPath) {
    return <Navigate to={redirectPath} replace />;
  }

  return null;
};

function App() {
logger.log('app render')
// console log the process env env vars available
// logger.log('process.env', process.env);

LicenseInfo.setLicenseKey('7d4c2c7bb19adccf81c160e453b4d37aTz0xMzQzNDEsRT0xODI1Mjg2Mzk5MDAwLFM9cHJvLExNPWFubnVhbCxQVj1RMy0yMDI0LEtWPTI=');
const globalCompRef = useRef({});
const compStreamObject = useRef({properties: []}); // this is used to see if we should update our compsheet, optimizedcomps, and negotiation Obj
const {user, townView, loading: authLoading} = useUser();
const {courtDates} = useCourtDates();
const {pathname} = useLocation();


// Add effect to handle initial auth check and smooth navigation
  // Show loading state while checking auth
  if (authLoading) {
    return (
      <Box 
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh'
        }}
      >
        <Card 
          sx={{
            p: 4,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 2,
            minWidth: 250  // Add minimum width to prevent card from being too narrow
          }}
        >
          <CircularProgress size={40} sx={{ color: 'primary.main' }} /> {/* Adjust size and color */}
          <Box sx={{ 
            mt: 2, 
            color: 'text.secondary',
            typography: 'body1'  // Add typography style for consistent text
          }}>
            Validating user info...
          </Box>
        </Card>
      </Box>
    );
  }

  // Snap is staff-only. A town user on a link stays in the town portal.
  if (pathname === '/snap' && user?.isLinkAuthenticated) {
    return (
      <Navigate
        to={townView ? `/${user.client_name || 'Aventine'}` : '/analysis'}
        replace
      />
    );
  }

  if (pathname === '/snap') {
    return (
      <>
        <AuthRedirectHandler />
        <div className="main-content !h-[100dvh] !max-h-[100dvh] min-h-0 flex flex-col overflow-hidden">
          <div className="sticky top-0 left-0 right-0 z-50 bg-white shadow shrink-0">
            <ResponsiveAppBar />
          </div>
          <div className="app-container w-full !h-auto flex-1 min-h-0">
            <Suspense fallback={<LoadingFallback />}>
              <SnapPage />
            </Suspense>
          </div>
        </div>
      </>
    );
  }

  // Handle link-authenticated users - show town view container within app context
  if (user?.isLinkAuthenticated && townView) {
    return (
      <>
        <AuthRedirectHandler />
        <div className="main-content town-view-shell">
          {/* Sticky Top Navigation */}
          <div className="sticky top-0 left-0 right-0 z-50 bg-white shadow shrink-0">
            <ResponsiveAppBar />
          </div>

          {/* Main Content Area */}
          <div className="app-container w-full">
            <Suspense fallback={<LoadingFallback />}>
              <Routes>
                <Route path="/signin" element={<SignIn />} />
                <Route path="/:clientName" element={<TownViewContainer />} />
                <Route path="/:clientName/:reviewid" element={<TownViewContainer />} />
                <Route path="*" element={<TownViewContainer />} />
              </Routes>
            </Suspense>
          </div>
        </div>
      </>
    );
  }
  
  // No longer need this harsh redirect with protected routes (private routes should be used to prevent access)
//   if (!user && window.location.pathname !== '/signin' && window.location.pathname !== '/save-taxes') {
//     return <Navigate to="/signin" replace />;
//   }
  
  // Nassau placeholder contract for now
const contractContent = `
<p>
I (Client) hereby authorize Aventine Properties LLC (Aventine) as my exclusive agent to process my Application For Correction/Refund for the 2026/2027 Assessment Roll with the Nassau County Assessment Review Commission and to represent me before any county and/or village Assessment Review Commission and/or the Small Claims Assessment Review of the Supreme Court, State of New York in order to obtain a reduction in the assessment appearing on the tentative roll published in Jan. 2025 and all corrections for the 2026/2027 tax year. I authorize ARC, Nassau County Department of Assessment and/or village/city to communicate directly with Aventine in all matters relating to this application.
</p>

<br/>

<p>
<strong>1. ELIGIBILITY:</strong> You must be a person named in the records of the Nassau County Clerk as a homeowner or that person's authorized agent or the estate of a deceased homeowner in order to receive a tax assessment reduction and a property tax reduction. Additionally, the property must be a one, two or three-family home used exclusively for residential purposes and taxes must be current. If you meet these eligibility conditions, you may sign this agreement.
</p>

<br/>

<p>
<strong>2. SERVICE TO BE PERFORMED:</strong> Note that you are not required by law to use a tax reduction service in order to file for and/or receive a tax assessment reduction. By signing this agreement, however, you are directing Aventine (not affiliated with any municipality) to prepare and file your First Level Complaint for review by the Assessment Review Commission, to represent you at any proceeding which includes the preparation and filing of the Petitions, the preparation and presentation of a Market Analysis or Appraisal, and to physically appear on your behalf at settlement conferences and all court hearings as necessary.
</p>

<br/>

<p>
<strong>3. NOTIFICATION OF TAX REDUCTION:</strong> Aventine will make a very reasonable effort to communicate the terms of any offer of settlement made by Nassau County in the course of the tax assessment review proceeding as required by law. I fully authorize Aventine to negotiate a settlement for me.
</p>

<br/>

<p>
<strong>4. FEE FOR SERVICE – THERE IS NO FEE UNLESS THERE IS A REDUCTION.</strong> If successful, however, I agree to pay a fee equal to 50% of the first year's Tax Reduction payable within 30 days of the notification of the tax reductions. If payment is not made within 30 days of the notification to the Client, a fee of 75% of that reduction will be charged. I agree to pay such fee in the event I sell or move out of the subject property. Interest of 1.5% per month will accrue from 31 days from the date on the original invoice on any unpaid balances and, if needed, reasonable collection fees and attorney fees incurred by Aventine. A $30 court filing fee plus a $75 appraisal fee may be added to the fee for a successful appeal only.
</p>

<br/>

<p>
<strong>5. MISCELLANEOUS:</strong> At any time within three (3) days after entering into this contract, I have the complete right to cancel this agreement in writing.
</p>
`;

return (
    <>
        <AuthRedirectHandler />
        <div className="main-content">
        {/* Sticky Top Navigation */}
        <div className="sticky top-0 left-0 right-0 z-50 bg-white shadow">
            <ResponsiveAppBar />
        </div>

        {/* Main Content Area */}
        <div className="app-container w-full">
            <Suspense fallback={<LoadingFallback />}>
              <Routes>
                  <Route path="/chat" element={<PrivateRoute element={<ChatContainer />} />} />
                  <Route path="/chat/:conversationID" element={<PrivateRoute element={<ChatContainer />} />} />
                  {/*  update this with our thing */}
                  {/* <Route path="/save-taxes" element={<PropertyTaxWidget
                      contractContent={contractContent}
                      contractType="text"
                      // environment="production" // comment out after testing
                      environment="staging"
                      onFormSubmit={() => {logger.log('custom form submission callback')}} // Optional callback when form is submitted
                      companyName="Propriety"
                   />} /> */}
                  <Route path="/signin" element={<SignIn />} />
                  <Route path="/test-link-auth" element={<LinkAuthTest />} />
                  {/* <Route path="/contract" element={<ServiceContract />} /> */}
                  
                  {/* Redirect from / and /table to /analysis if logged in */}
                  <Route path="/" element={user ? <Navigate to="/analysis" replace /> : <WebpageHome />} />
                  <Route path="/table" element={<Navigate to="/analysis" replace />} />
                  
                  <Route
                      path="/analysis"
                      element={
                        <PrivateRoute element={
                          <Suspense fallback={<LoadingFallback />}>
                            <GoogleMapsProvider>
                              <CaseAnalysisContainer courtDates={courtDates || []} compStreamObject={compStreamObject} globalCompRef={globalCompRef}/>
                            </GoogleMapsProvider>
                          </Suspense>
                        }/>
                      }
                  />
                  <Route path="/analysis/:reviewid" element={
                    <PrivateRoute element={
                      <Suspense fallback={<LoadingFallback />}>
                        <GoogleMapsProvider>
                          <IndividualAnalysisContainer compStreamObject={compStreamObject} globalCompRef={globalCompRef}/>
                        </GoogleMapsProvider>
                      </Suspense>
                    } />
                  } />
                  <Route path="/graph"
                      element={
                          <PrivateRoute
                              element={<Visualizations />}
                          />
                      }
                  />
                  <Route
                    path="/reports"
                    element={
                      <PrivateRoute
                        element={
                          <Suspense fallback={<LoadingFallback />}>
                            <InvoicingAnalysisPage />
                          </Suspense>
                        }
                      />
                    }
                  />
              </Routes>
            </Suspense>
        </div>
    </div>
    </>
);
}

// Only enable WDYR tracking in development
if (process.env.NODE_ENV === 'development') {
  App.whyDidYouRender = true;
}
// Update for push

export default App;